/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Charyung Kim (crkim@rsupport.com)
 *
 * @description
 *
 * @created at Tue Dec 10 2024
 **/

import useWhiteboardStore from "../../../stores/useWhiteboardStore";

export function Cursor({ color }: { color: string }) {
	return (
		<svg width="16" height="21" viewBox="0 0 16 21" fill="none" xmlns="http://www.w3.org/2000/svg">
			<path d="M0 21V0L15.5 14L6.5 15L0 21Z" fill={color} />
		</svg>
	);
}

export function Pointer({ color }: { color: string }) {
	const isMouseDown = useWhiteboardStore((state) => state.isMouseDown);
	return (
		<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
			{isMouseDown && (
				<circle
					cx="10"
					cy="10"
					r="9.75"
					fill={color}
					fillOpacity="0.1"
					stroke={color}
					strokeOpacity="0.6"
					strokeWidth="0.5"
				/>
			)}
			<path
				d="M16.5 10C16.5 13.5899 13.5899 16.5 10 16.5C6.41015 16.5 3.5 13.5899 3.5 10C3.5 6.41015 6.41015 3.5 10 3.5C13.5899 3.5 16.5 6.41015 16.5 10Z"
				fill={color}
			/>
			<circle cx="10" cy="10" r="2.5" fill="white" fillOpacity="0.88" />
		</svg>
	);
}

export function PointerActive({ color }: { color: string }) {
	return (
		<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
			<circle
				cx="10"
				cy="10"
				r="9.75"
				fill={color}
				fillOpacity="0.1"
				stroke={color}
				strokeOpacity="0.6"
				strokeWidth="0.5"
			/>
			<path
				d="M16.5 10C16.5 13.5899 13.5899 16.5 10 16.5C6.41015 16.5 3.5 13.5899 3.5 10C3.5 6.41015 6.41015 3.5 10 3.5C13.5899 3.5 16.5 6.41015 16.5 10Z"
				fill={color}
			/>
			<circle cx="10" cy="10" r="2.5" fill="white" fillOpacity="0.88" />
		</svg>
	);
}

export function Freeline({ color }: { color: string }) {
	return (
		<svg width="5" height="5" viewBox="0 0 5 5" fill="none" xmlns="http://www.w3.org/2000/svg">
			<circle cx="2.5" cy="2.5" r="2.5" fill={color} />
		</svg>
	);
}

export function Line({ color }: { color: string }) {
	return (
		<svg width="17" height="26" viewBox="0 0 17 26" fill="none" xmlns="http://www.w3.org/2000/svg">
			<path d="M0 14V0L10.3333 9.33333L4.33333 10L0 14Z" fill={color} />
			<path d="M5 25L16 14" stroke={color} strokeLinecap="round" />
		</svg>
	);
}

export function Arrow({ color }: { color: string }) {
	return (
		<svg width="17" height="26" viewBox="0 0 17 26" fill="none" xmlns="http://www.w3.org/2000/svg">
			<path d="M0 14V0L10.3333 9.33333L4.33333 10L0 14Z" fill={color} />
			<path
				d="M4.64645 24.6464C4.45118 24.8417 4.45118 25.1583 4.64645 25.3536C4.84171 25.5488 5.15829 25.5488 5.35355 25.3536L4.64645 24.6464ZM16.5 14C16.5 13.7239 16.2761 13.5 16 13.5H11.5C11.2239 13.5 11 13.7239 11 14C11 14.2761 11.2239 14.5 11.5 14.5H15.5V18.5C15.5 18.7761 15.7239 19 16 19C16.2761 19 16.5 18.7761 16.5 18.5V14ZM5.35355 25.3536L16.3536 14.3536L15.6464 13.6464L4.64645 24.6464L5.35355 25.3536Z"
				fill={color}
			/>
		</svg>
	);
}

export function Marker({ color }: { color: string }) {
	return (
		<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
			<circle opacity="0.6" cx="12" cy="12" r="12" fill={color} />
		</svg>
	);
}

export function Circle({ color }: { color: string }) {
	return (
		<svg width="18" height="27" viewBox="0 0 18 27" fill="none" xmlns="http://www.w3.org/2000/svg">
			<path
				d="M17 19.5C17 23.0899 14.0899 26 10.5 26C6.91015 26 4 23.0899 4 19.5C4 15.9101 6.91015 13 10.5 13C14.0899 13 17 15.9101 17 19.5Z"
				stroke={color}
			/>
			<path d="M0 14V0L10.3333 9.33333L4.33333 10L0 14Z" fill={color} />
		</svg>
	);
}

export function Rect({ color }: { color: string }) {
	return (
		<svg width="17" height="26" viewBox="0 0 17 26" fill="none" xmlns="http://www.w3.org/2000/svg">
			<path d="M0 14V0L10.3333 9.33333L4.33333 10L0 14Z" fill={color} />
			<rect x="3.5" y="12.5" width="13" height="13" rx="1.5" stroke={color} />
		</svg>
	);
}
